import type { ShotInput, SportsGameSettings } from '../../sports.types';
import type {
  ISportDefinition,
  SportAssets,
  SportPhysicsParams,
  StartingPosition,
  ShotEvaluation,
} from '../sport-definition.interface';

// ─── Physics constants ─────────────────────────────────────────────────────────

const PHYSICS: SportPhysicsParams = {
  ballRadius: 0.12,             // NBA ball ≈ 24 cm diameter
  launchSpeedScale: 5.5,
  arcHeightFactor: 1.4,         // High looping arc — classic basketball rainbow
  assistCorrectionStrength: 0.65,
  goalToleranceRadius: 0.22,    // NBA hoop inner radius ≈ 22 cm
  optimalElevationDeg: 50,
};

// ─── Starting positions ────────────────────────────────────────────────────────

/**
 * 5 positions arranged in an arc around the hoop.
 * Positions 1–3 are inside the 3-point line (2 pts when enable3PointLine: true).
 * Positions 4–5 are outside the arc (3 pts when enable3PointLine: true).
 */
const STARTING_POSITIONS: StartingPosition[] = [
  { lateralOffset:  0.0, distance: 1.5, pointsIfScored: 2 }, // free-throw line, center
  { lateralOffset: +0.8, distance: 2.0, pointsIfScored: 2 }, // right elbow
  { lateralOffset: -0.8, distance: 2.0, pointsIfScored: 2 }, // left elbow
  { lateralOffset: +1.2, distance: 2.8, pointsIfScored: 3 }, // right corner 3
  { lateralOffset: -1.2, distance: 2.8, pointsIfScored: 3 }, // left corner 3
];

// ─── Assets ────────────────────────────────────────────────────────────────────

const ASSETS: SportAssets = {
  // Phase 3+: ballModelPath: 'assets/sports/basketball/basketball.glb'
  ballColor: '#E55B00', // classic NBA orange
};

// ─── Sport definition ──────────────────────────────────────────────────────────

export class BasketballSportDefinition implements ISportDefinition {
  readonly id = 'basketball' as const;
  readonly displayName = 'Basketball';
  readonly scoringModel = 'position' as const;
  readonly assets = ASSETS;
  readonly physicsParams = PHYSICS;
  readonly startingPositions = STARTING_POSITIONS;
  readonly maxAttempts = 5;

  readonly arCameraHint = { pitch: 15, yaw: 0 };
  // AR marker is wall-mounted behind the backboard; scene lies flat on horizontal plane
  readonly arSceneRotation: [number, number, number] = [-Math.PI / 2, 0, 0];

  evaluateShot(
    input: ShotInput,
    assist: number,
    settings: SportsGameSettings
  ): ShotEvaluation {
    const {
      launchSpeedScale,
      optimalElevationDeg = 50,
      goalToleranceRadius,
      assistCorrectionStrength,
    } = this.physicsParams;

    // ── Step 1: Whiff check ──────────────────────────────────────────────────
    if (input.overSwing) {
      return {
        scored: false,
        pointsEarned: 0,
        finalPosition: { x: 0, y: 0, z: 0 },
        isWhiff: true,
      };
    }

    // ── Step 2: Trajectory ───────────────────────────────────────────────────
    const launchAngleDeg = input.direction.y * optimalElevationDeg * 2;
    const launchAngleRad = (launchAngleDeg * Math.PI) / 180;
    const baseDistance = input.power * launchSpeedScale * Math.cos(launchAngleRad);

    // ── Step 3: Assist correction + noise ────────────────────────────────────
    const rawLateral = input.direction.x;
    const correctedLateral = rawLateral * (1 - assist * assistCorrectionStrength);
    const lateralNoise = (Math.random() * 2 - 1) * 0.05 * (1 - assist);
    const finalX = correctedLateral * 0.4 + lateralNoise;

    // Distance noise
    const distanceNoise = (Math.random() * 2 - 1) * 0.08 * (1 - assist);
    const finalDistance = Math.max(0, baseDistance + distanceNoise);

    // ── Step 4: Hit check ────────────────────────────────────────────────────
    // Basketball: both lateral AND distance must be within tolerance
    const lateralHit = Math.abs(finalX) <= goalToleranceRadius;
    // Distance tolerance: ±15% of the current attempt's distance
    // (The renderer passes current position distance via settings — we use a
    //  generous window here since the player can't control exact distance.)
    const distanceTolerance = goalToleranceRadius * 2.5;
    const distanceHit = Math.abs(finalDistance - baseDistance) <= distanceTolerance;
    const scored = lateralHit && distanceHit;

    // ── Step 5: Points (position-based vs flat) ──────────────────────────────
    // The pointsIfScored for the current position is looked up by the renderer
    // from StartingPosition.pointsIfScored and merged into settings.pointsPerSuccess.
    // Here we honour the current settings value which the renderer already resolved.
    const pointsEarned = scored ? settings.pointsPerSuccess : 0;

    return {
      scored,
      pointsEarned,
      // y: 2.05 matches the hoop ring position in sports-scene.component.ts
      finalPosition: { x: finalX, y: 2.05, z: -finalDistance },
      isWhiff: false,
    };
  }
}
